import { ChangeEvent, FC, useContext, useMemo, useRef, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'
import { GridPanel, GridPanelProps } from '@microservices/wiskey-react-table-component'

import { PageContext } from '@pages/ConfiguredEntity'

type VisibilityModel = Record<string, boolean>

const isColumnVisible = (model: VisibilityModel, field: string) => model[field] !== false

export const CustomPanelComponent: FC<GridPanelProps> = props => {
  const { showActionsColumn, hasRowAction, visibleColumns, apiRef, handleChangeColumnVisibilityModel } =
    useContext(PageContext)
  const [searchValue, setSearchValue] = useState('')
  const initialVisibilityModelRef = useRef<VisibilityModel>()

  const [uiVisibilityModel, setUiVisibilityModel] = useState<VisibilityModel>(
    () => apiRef.current?.state?.columns?.columnVisibilityModel ?? {}
  )
  const uiVisibilityModelRef = useRef(uiVisibilityModel)
  uiVisibilityModelRef.current = uiVisibilityModel

  if (!initialVisibilityModelRef.current) {
    initialVisibilityModelRef.current = { ...uiVisibilityModelRef.current }
  }

  const rows = useMemo(
    () =>
      [...(visibleColumns || [])]
        .filter(column => {
          if (column.code === 'linkToForm' && hasRowAction) {
            return false
          }

          if (column.code === 'actions' && showActionsColumn) {
            return false
          }

          return true
        })
        .sort((a, b) => Number(a.restrictions?.order ?? 0) - Number(b.restrictions?.order ?? 0))
        .filter(column => {
          if (!searchValue.trim()) {
            return true
          }

          const search = searchValue.toLowerCase()

          return (
            column.code.toLowerCase().includes(search) || column.title?.toLowerCase().includes(search)
          )
        }),
    [hasRowAction, searchValue, showActionsColumn, visibleColumns]
  )

  const applyVisibilityModel = (nextModel: VisibilityModel) => {
    uiVisibilityModelRef.current = nextModel
    setUiVisibilityModel(nextModel)
    handleChangeColumnVisibilityModel(nextModel)
  }

  const updateVisibilityModel = (
    updater: VisibilityModel | ((prevModel: VisibilityModel) => VisibilityModel)
  ) => {
    const prevModel = uiVisibilityModelRef.current
    const nextModel = typeof updater === 'function' ? updater(prevModel) : updater

    applyVisibilityModel(nextModel)
  }

  const handleToggleColumn = (field: string, checked: boolean) => {
    updateVisibilityModel(prevModel => ({ ...prevModel, [field]: checked }))
  }

  const hideableRows = rows.filter(column => apiRef.current?.getColumn(column.code)?.hideable !== false)

  const areAllHideableVisible = hideableRows.every(column => isColumnVisible(uiVisibilityModel, column.code))

  const handleToggleAll = (checked: boolean) => {
    updateVisibilityModel(prevModel => {
      const nextModel = { ...prevModel }

      hideableRows.forEach(column => {
        nextModel[column.code] = checked
      })

      return nextModel
    })
  }

  const handleReset = () => {
    applyVisibilityModel(initialVisibilityModelRef.current ?? {})
  }

  return (
    <GridPanel
      {...props}
      sx={{
        position: 'relative',
        zIndex: 1305,
        display: 'block',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', width: 360, maxWidth: '100%' }}>
        <TextField
          fullWidth
          size='small'
          placeholder='Search'
          value={searchValue}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchValue(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' />
              </InputAdornment>
            ),
          }}
          sx={{ p: 1 }}
        />
        <List
          dense
          sx={{
            maxHeight: 320,
            overflowY: 'auto',
            px: 1,
            pt: 0,
            pb: 1,
            '& .MuiListItem-root': {
              py: 0,
            },
          }}
        >
          {rows.map(column => {
            const field = column.code
            const checked = isColumnVisible(uiVisibilityModel, field)
            const isDisabled = apiRef.current?.getColumn(field)?.hideable === false

            return (
              <ListItem key={field} dense disableGutters sx={{ minHeight: 28 }}>
                <FormControlLabel
                  sx={{
                    width: '100%',
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      width: '100%',
                    },
                  }}
                  control={
                    <Checkbox
                      checked={checked}
                      disabled={isDisabled}
                      onChange={event => handleToggleColumn(field, event.target.checked)}
                      size='small'
                      sx={{ p: 0.5, mr: 0.5 }}
                    />
                  }
                  label={
                    <ListItemText
                      primary={column.title || column.code}
                      primaryTypographyProps={{ variant: 'body2' }}
                      sx={{ m: 0 }}
                    />
                  }
                />
              </ListItem>
            )
          })}
        </List>
        <Divider />
        <ListItem dense disableGutters sx={{ px: 1, py: 0.5 }}>
          <Checkbox
            checked={areAllHideableVisible}
            indeterminate={!areAllHideableVisible}
            onChange={event => handleToggleAll(event.target.checked)}
          />
          <Typography variant='body2'>Show/Hide All</Typography>
          <IconButton size='small' sx={{ ml: 'auto' }} onClick={handleReset}>
            <RestartAltIcon fontSize='small' />
            <Typography sx={{ ml: 0.5 }} variant='caption'>
              RESET
            </Typography>
          </IconButton>
        </ListItem>
      </Box>
    </GridPanel>
  )
}
